import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ACTIVE_CASES_PAGE_SIZE,
  DIRECTORY_PAGE_SIZE,
  persistAdvisorCasesListReturn,
  useCasesListPageFromUrl,
  useDirectoryListPageFromUrl,
} from '../utils/casesListUrl';
import TopNav from '../components/feature/TopNav';
import { useAdvisorProject } from '../contexts/AdvisorProjectContext';
import { loadAdvisorEssentialData, loadAdvisorDirectoryData } from '../services/advisorApiAdapter';
import { mvpApi } from '../services/api';
import i18n from '../i18n';
import { showPopupMessage } from '../utils/showPopup';
import AdvisorApplicationsTable from '../components/advisor/AdvisorApplicationsTable';
import AdvisorWorkbenchBrief from '../components/advisor/AdvisorWorkbenchBrief';
import AdvisorCustomerDirectoryTable from '../components/advisor/AdvisorCustomerDirectoryTable';
import CasesListPagination from '../components/advisor/CasesListPagination';
import { isUnifiedRegularization2026FormName, UNIFIED_CASE_TYPE_LABEL_EN } from '../utils/unifiedCaseTypeLabel';
import { SkeletonApplicationsTable, SkeletonDirectoryTable } from '../components/advisor/SkeletonLoader';
import { getProcedureBundle, pickEn } from '../procedures/procedureContent';

/** Cases explicitly marked "needs attention" on the case detail screen (Mongo needs_attention). */
function advisorCaseNeedsAttention(c) {
  return Boolean(c.needs_attention);
}

const ACTIVE_CASE_TYPE = UNIFIED_CASE_TYPE_LABEL_EN;

/** Sticky top nav uses `h-14` (3.5rem = 56px). Compact filters sit directly under it. */
const ADVISOR_STICKY_TOP_PX = 56;

const STATUS_FILTER_OPTIONS = [
  { value: 'All', labelKey: 'advisorPortal.allStatuses' },
  { value: 'new', labelKey: 'advisorPortal.statusNew' },
  { value: 'in_progress', labelKey: 'advisorPortal.statusInProgress' },
  { value: 'pending_review', labelKey: 'advisorPortal.statusPendingReview' },
  { value: 'waiting_on_client', labelKey: 'advisorPortal.statusWaitingOnClient' },
  { value: 'waiting_on_documents', labelKey: 'advisorPortal.statusWaitingOnDocuments' },
  { value: 'ready_to_submit', labelKey: 'advisorPortal.statusReadyToSubmit' },
  { value: 'submitted', labelKey: 'advisorPortal.statusSubmitted' },
  { value: 'in_review_govt', labelKey: 'advisorPortal.statusInReviewGovernment' },
  { value: 'approved', labelKey: 'advisorPortal.statusApproved' },
  { value: 'rejected', labelKey: 'advisorPortal.statusRejected' },
  { value: 'cancelled_client', labelKey: 'advisorPortal.statusCancelledClient' },
  { value: 'cancelled_internal', labelKey: 'advisorPortal.statusCancelledInternal' },
  { value: 'expired_abandoned', labelKey: 'advisorPortal.statusExpiredAbandoned' },
];

function normalizeStatusKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return '';
  if (s === 'new') return 'new';
  if (s.includes('in review') || s.includes('govt') || s.includes('government')) return 'in_review_govt';
  if (s.includes('pending') && s.includes('review')) return 'pending_review';
  if (s.includes('waiting') && s.includes('document')) return 'waiting_on_documents';
  if (s.includes('waiting') && s.includes('client')) return 'waiting_on_client';
  if (s.includes('ready') && s.includes('submit')) return 'ready_to_submit';
  if (s.includes('submitted')) return 'submitted';
  if (s.includes('approved')) return 'approved';
  if (s.includes('rejected')) return 'rejected';
  if (s.includes('cancelled') && s.includes('client')) return 'cancelled_client';
  if (s.includes('cancelled') && (s.includes('internal') || s.includes('advisor'))) return 'cancelled_internal';
  if (s.includes('expired') || s.includes('abandoned')) return 'expired_abandoned';
  if (s.includes('progress')) return 'in_progress';
  return s.replace(/\s+/g, '_');
}

/** Fully-contained custom dropdown - options list stays inside the drawer. */
function StatusDropdown({ value, onChange, options, t: translate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-12 flex items-center justify-between pl-4 pr-4 text-sm font-medium border rounded-xl bg-slate-50 text-slate-800 transition-all ${open ? 'border-emerald-400 ring-2 ring-emerald-500/20' : 'border-slate-100'
          }`}
      >
        <span className="truncate">{translate(selected.labelKey)}</span>
        <i className={`ri-arrow-down-s-line text-slate-400 text-lg shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-10 left-0 right-0 top-[calc(100%+4px)] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <ul className="max-h-52 overflow-y-auto overscroll-contain">
            {options.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${o.value === value
                    ? 'bg-emerald-50 text-emerald-700 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  {translate(o.labelKey)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Fully-contained custom dropdown for advisor view - handles grouped options. */
function AdvisorViewDropdown({ value, onChange, myLabel, allLabel, groupLabel, advisorOptions, t: translate, advisorNumberKey }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const getLabel = (v) => {
    if (v === 'my') return myLabel;
    if (v === 'all') return allLabel;
    if (String(v).startsWith('advisor:')) {
      const id = String(v).replace(/^advisor:/, '');
      const a = advisorOptions.find((x) => String(x.id) === id);
      return a ? (a.name || a.email || translate(advisorNumberKey, { id: a.id })) : v;
    }
    return v;
  };

  const allItems = [
    { value: 'my', label: myLabel, group: null },
    ...advisorOptions.map((a) => ({
      value: `advisor:${a.id}`,
      label: a.name || a.email || translate(advisorNumberKey, { id: a.id }),
      group: groupLabel,
    })),
    { value: 'all', label: allLabel, group: null },
  ];

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-12 flex items-center justify-between pl-4 pr-4 text-sm font-medium border rounded-xl bg-slate-50 text-slate-800 transition-all ${open ? 'border-emerald-400 ring-2 ring-emerald-500/20' : 'border-slate-100'}`}
      >
        <span className="truncate">{getLabel(value)}</span>
        <i className={`ri-arrow-down-s-line text-slate-400 text-lg shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-10 left-0 right-0 top-[calc(100%+4px)] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <ul className="max-h-56 overflow-y-auto overscroll-contain">
            {allItems.map((item, idx) => {
              const prevGroup = idx > 0 ? allItems[idx - 1].group : null;
              const showGroupHeader = item.group && item.group !== prevGroup;
              return (
                <React.Fragment key={item.value}>
                  {showGroupHeader && (
                    <li className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      {item.group}
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      onClick={() => { onChange(item.value); setOpen(false); }}
                      className={`w-full text-left px-4 ${item.group ? 'pl-6' : 'pl-4'} py-2.5 text-sm transition-colors ${item.value === value ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      {item.label}
                    </button>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}


export default function AdvisorCustomerSearch() {
  return <AdvisorCustomerSearchInner />;
}

function AdvisorCustomerSearchInner() {
  const { t } = useTranslation();
  const { activeProjectId, loading: projectsLoading, projects, activeProject } = useAdvisorProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page: casesPage, setPage: setCasesPage, returnTo: casesListReturnTo } = useCasesListPageFromUrl();
  const { page: dirPage, setPage: setDirPage } = useDirectoryListPageFromUrl();
  const [loading, setLoading] = useState(true);
  const [casesLoading, setCasesLoading] = useState(false);
  const [tab, setTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [casesTotal, setCasesTotal] = useState(0);
  const [casesTotalPages, setCasesTotalPages] = useState(0);
  const [casesSummary, setCasesSummary] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [meId, setMeId] = useState(null);
  const [advisorName, setAdvisorName] = useState('Advisor');
  const [advisorView, setAdvisorView] = useState('all');
  const [query, setQuery] = useState('');
  const [opStatusFilter, setOpStatusFilter] = useState('All');
  const [appTypeFilter] = useState(ACTIVE_CASE_TYPE);
  // The case-type badge defaults to the Spain regularization label, but shows the
  // selected production's actual procedure (e.g. "Entertainment/artistic work entry")
  // when an org/LP production with a resolved procedure_slug is active.
  const [caseTypeLabel, setCaseTypeLabel] = useState(ACTIVE_CASE_TYPE);
  const [advisorFilter, setAdvisorFilter] = useState('All');
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [statusDateSort, setStatusDateSort] = useState('desc');
  const [dirQuery, setDirQuery] = useState('');
  const [dirStatus, setDirStatus] = useState('all');
  const [dirTotal, setDirTotal] = useState(0);
  const [dirTotalPages, setDirTotalPages] = useState(0);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const isInitialMount = useRef(true);
  const activeSearchId = useId();
  const activeStatusFilterId = useId();
  const activeSortId = useId();
  const activeFiltersCompactSearchId = useId();
  const activeFiltersBlockRef = useRef(null);
  const [activeFiltersStickyCompact, setActiveFiltersStickyCompact] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const alert = searchParams.get('alert');
    if (!alert) return;
    setTab('active');
    if (alert === 'ready') {
      setOpStatusFilter('ready_to_submit');
    } else if (alert === 'attention') {
      setFollowUpOnly(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('alert');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const slug = activeProject?.procedure_slug;
    // A production not scoped to the Spain regularization program shouldn't show that
    // label even before its procedure_slug is backfilled - fall back to its industry.
    const orgFallback = activeProject?.industry
      ? `${activeProject.industry.replace(/_/g, ' ')} crew`.replace(/^\w/, (c) => c.toUpperCase())
      : ACTIVE_CASE_TYPE;
    if (!slug) {
      setCaseTypeLabel(activeProject ? orgFallback : ACTIVE_CASE_TYPE);
      return undefined;
    }
    let cancelled = false;
    getProcedureBundle(slug)
      .then((bundle) => {
        if (cancelled) return;
        const title = pickEn(bundle?.card?.procedure?.title);
        setCaseTypeLabel(title || orgFallback);
      })
      .catch(() => {
        if (!cancelled) setCaseTypeLabel(orgFallback);
      });
    return () => { cancelled = true; };
  }, [activeProject?.procedure_slug, activeProject?.industry]);

  const buildCasesParams = useCallback(() => {
    const params = {
      page: casesPage,
      page_size: ACTIVE_CASES_PAGE_SIZE,
      sort: statusDateSort === 'asc' ? 'status_date_asc' : 'status_date_desc',
    };
    const q = query.trim();
    if (q) params.q = q;
    if (opStatusFilter && opStatusFilter !== 'All') {
      params.status = opStatusFilter;
    }
    if (unassignedOnly) params.unassigned_only = true;
    if (followUpOnly) params.needs_attention_only = true;
    if (advisorView === 'my' && meId != null) {
      params.assigned_advisor_id = meId;
    } else if (String(advisorView).startsWith('advisor:')) {
      const id = Number(String(advisorView).replace(/^advisor:/, ''));
      if (!Number.isNaN(id)) params.assigned_advisor_id = id;
    } else if (advisorView === 'all' && advisorFilter !== 'All') {
      const fid = Number(advisorFilter);
      if (!Number.isNaN(fid)) params.assigned_advisor_id = fid;
    }
    if (activeProjectId != null) {
      params.project_id = activeProjectId;
    }
    return params;
  }, [
    casesPage,
    query,
    opStatusFilter,
    unassignedOnly,
    followUpOnly,
    advisorView,
    advisorFilter,
    meId,
    statusDateSort,
    activeProjectId,
  ]);

  const loadData = useCallback((opts = {}) => {
    const { fullPage = false, silent = false } = opts;
    if (fullPage) setLoading(true);
    else if (!silent) setCasesLoading(true);

    return loadAdvisorEssentialData({ casesParams: buildCasesParams() })
      .then(({ cases: c, me, advisors: adv, casesMeta }) => {
        setCases(c);
        setAdvisors(Array.isArray(adv) ? adv : []);
        if (casesMeta) {
          setCasesTotal(casesMeta.total_cases ?? 0);
          setCasesTotalPages(casesMeta.total_pages ?? 0);
          setCasesSummary(casesMeta.summary ?? null);
        }
        const mid = me?.id ?? me?.user_id;
        setMeId(mid != null && mid !== '' ? Number(mid) : null);
        const full = [me.first_name, me.last_name].filter(Boolean).join(' ').trim();
        setAdvisorName(full || me.email || i18n.t('advisorPortal.advisorNameFallback'));
      })
      .catch(() => {
        if (fullPage) {
          setCases([]);
          setCasesTotal(0);
          setCasesTotalPages(0);
          setCasesSummary(null);
        }
      })
      .finally(() => {
        if (fullPage) setLoading(false);
        if (!silent) setCasesLoading(false);
      });
  }, [buildCasesParams]);

  const filterSnapshotRef = useRef(null);
  useEffect(() => {
    const snap = JSON.stringify([
      query,
      opStatusFilter,
      advisorView,
      advisorFilter,
      followUpOnly,
      unassignedOnly,
      statusDateSort,
    ]);
    if (filterSnapshotRef.current === null) {
      filterSnapshotRef.current = snap;
      return;
    }
    if (filterSnapshotRef.current === snap) return;
    filterSnapshotRef.current = snap;
    setCasesPage(1);
  }, [query, opStatusFilter, advisorView, advisorFilter, followUpOnly, unassignedOnly, statusDateSort, setCasesPage]);

  useEffect(() => {
    persistAdvisorCasesListReturn(casesListReturnTo);
  }, [casesListReturnTo]);

  const buildDirectoryParams = useCallback(() => {
    const params = {
      page: dirPage,
      page_size: DIRECTORY_PAGE_SIZE,
    };
    const q = dirQuery.trim();
    if (q) params.q = q;
    if (dirStatus && dirStatus !== 'all') params.status = dirStatus;
    if (activeProjectId != null) {
      params.project_id = activeProjectId;
    }
    return params;
  }, [dirPage, dirQuery, dirStatus, activeProjectId]);

  const loadDirectory = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setDirectoryLoading(true);
    return loadAdvisorDirectoryData(buildDirectoryParams())
      .then(({ users: u, total, total_pages: totalPages }) => {
        setUsers(u);
        setDirTotal(total ?? 0);
        setDirTotalPages(totalPages ?? 0);
      })
      .catch(() => {
        if (!silent) {
          setUsers([]);
          setDirTotal(0);
          setDirTotalPages(0);
        }
      })
      .finally(() => {
        if (!silent) setDirectoryLoading(false);
      });
  }, [buildDirectoryParams]);

  useEffect(() => {
    if (tab !== 'active') return;
    if (projectsLoading || activeProjectId == null) return;
    const fullPage = isInitialMount.current;
    loadData({ fullPage, silent: false });
    if (fullPage) isInitialMount.current = false;
  }, [tab, loadData, projectsLoading, activeProjectId]);

  useEffect(() => {
    if (tab !== 'directory') return;
    if (projectsLoading || activeProjectId == null) return;
    loadDirectory();
  }, [tab, loadDirectory, projectsLoading, activeProjectId]);

  /** On desktop, show the compact toolbar after the full filter card scrolls away. Mobile keeps it visible. */
  useEffect(() => {
    if (tab !== 'active') {
      setActiveFiltersStickyCompact(false);
      return undefined;
    }
    let raf = 0;
    const update = () => {
      const el = activeFiltersBlockRef.current;
      if (!el) {
        setActiveFiltersStickyCompact(false);
        return;
      }
      const rect = el.getBoundingClientRect();
      setActiveFiltersStickyCompact(rect.bottom < ADVISOR_STICKY_TOP_PX - 1);
    };
    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      cancelAnimationFrame(raf);
    };
  }, [tab]);

  useEffect(() => {
    const id = setInterval(() => {
      if (tab === 'active') {
        loadData({ silent: true });
      }
      if (tab === 'directory') {
        loadDirectory({ silent: true });
      }
    }, 60000);
    return () => clearInterval(id);
  }, [loadData, loadDirectory, tab]);

  /** Other registered admins (exclude current user) for the "Showing" dropdown. */
  const otherAdvisorOptions = useMemo(() => {
    return advisors
      .filter((a) => meId == null || Number(a.id) !== Number(meId))
      .slice()
      .sort((a, b) => String(a.name || a.email || '').localeCompare(String(b.name || b.email || '')));
  }, [advisors, meId]);

  /** All registered admins for the "All Applications" advisor sub-filter. */
  const allAdvisorsSorted = useMemo(() => {
    return advisors.slice().sort((a, b) => String(a.name || a.email || '').localeCompare(String(b.name || b.email || '')));
  }, [advisors]);

  useEffect(() => {
    if (advisorView === 'my' || advisorView === 'all' || String(advisorView).startsWith('advisor:')) return;
    const match = advisors.find((a) => (a.name || '').trim() === String(advisorView).trim());
    if (match) setAdvisorView(`advisor:${match.id}`);
  }, [advisors, advisorView]);

  useEffect(() => {
    if (advisorFilter === 'All') return;
    const n = Number(advisorFilter);
    if (!Number.isNaN(n)) return;
    const match = advisors.find((a) => (a.name || '').trim() === String(advisorFilter).trim());
    if (match) setAdvisorFilter(String(match.id));
  }, [advisors, advisorFilter]);

  const assignedLabel = useMemo(() => {
    if (advisorView === 'all') return null;
    if (advisorView === 'my') return advisorName;
    if (String(advisorView).startsWith('advisor:')) {
      const id = Number(String(advisorView).replace(/^advisor:/, ''));
      const a = advisors.find((x) => Number(x.id) === id);
      return (a?.name || a?.email || t('advisorPortal.advisorNameFallback')).trim() || advisorName;
    }
    return advisorView;
  }, [advisorName, advisorView, advisors]);

  const activeCaseKpis = useMemo(() => {
    const renewals = cases.filter(
      (c) =>
        Boolean(c.renewal_due || c.renewalDue) ||
        String(c.status || '').toLowerCase().includes('renew'),
    ).length;
    return {
      open: casesSummary?.filtered_total ?? casesTotal ?? 0,
      needsAttention: casesSummary?.needs_attention ?? 0,
      renewals,
      families: casesSummary?.family_groups ?? 0,
    };
  }, [casesSummary, casesTotal, cases]);

  const dirFilterSnapshotRef = useRef(null);
  useEffect(() => {
    const snap = JSON.stringify([dirQuery, dirStatus]);
    if (dirFilterSnapshotRef.current === null) {
      dirFilterSnapshotRef.current = snap;
      return;
    }
    if (dirFilterSnapshotRef.current === snap) return;
    dirFilterSnapshotRef.current = snap;
    setDirPage(1);
  }, [dirQuery, dirStatus, setDirPage]);

  useEffect(() => {
    if (dirTotalPages > 0 && dirPage > dirTotalPages) {
      setDirPage(dirTotalPages);
    }
  }, [dirPage, dirTotalPages, setDirPage]);

  const clearActiveCasesFilters = () => {
    setQuery('');
    setOpStatusFilter('All');
    setAdvisorFilter('All');
    setFollowUpOnly(false);
    setUnassignedOnly(false);
    setStatusDateSort('desc');
    setCasesPage(1);
  };

  const hasActiveFilters =
    query ||
    opStatusFilter !== 'All' ||
    (advisorView === 'all' && advisorFilter !== 'All') ||
    followUpOnly ||
    unassignedOnly ||
    statusDateSort !== 'desc';

  const handleDeleteUser = async (user) => {
    if (!user?.id) return;
    setDeleteModalUser(user);
  };

  const confirmDeleteUser = async () => {
    if (!deleteModalUser?.id) return;
    const user = deleteModalUser;
    setDeleteModalUser(null);
    setDeletingUserId(user.id);
    try {
      await mvpApi.deleteAdminUser(user.id);
      setCases((prev) => prev.filter((c) => String(c.user_id) !== String(user.id)));
      await loadDirectory({ silent: true });
    } catch (e) {
      showPopupMessage(t('advisorPortal.deleteUserFailed'), 'error');
    } finally {
      setDeletingUserId(null);
    }
  };

  const compactFiltersBar = (
    <div
      className={`sticky top-[56px] z-40 w-full bg-slate-50/80 backdrop-blur-md border-b border-slate-200/60 transition-all duration-300 sm:hidden`}
      style={{
        marginTop: '-1px',
        paddingTop: '8px',
        paddingBottom: '12px',
      }}
    >
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
          <input
            type="search"
            value={tab === 'active' ? query : dirQuery}
            onChange={(e) => tab === 'active' ? setQuery(e.target.value) : setDirQuery(e.target.value)}
            placeholder={tab === 'active' ? t('advisorPortal.compactSearchPh') : t('advisorPortal.dirSearchPlaceholder')}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 transition-all shadow-sm"
          />
        </div>
        <button
          onClick={() => setIsFilterDrawerOpen(true)}
          className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all active:scale-95 ${hasActiveFilters || (tab === 'directory' && (dirStatus !== 'all'))
            ? 'border-emerald-500 bg-emerald-50 text-emerald-600 shadow-sm'
            : 'border-slate-200 bg-white text-slate-600 shadow-sm'
            }`}
        >
          <i className="ri-equalizer-line text-xl" />
          {(hasActiveFilters || (tab === 'directory' && (dirStatus !== 'all'))) && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white ring-2 ring-white">
              !
            </span>
          )}
        </button>
      </div>
    </div>
  );

  const filterDrawer = isFilterDrawerOpen && (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="adv-drawer-overlay" onClick={() => setIsFilterDrawerOpen(false)} />
      <div className="adv-drawer-content w-full sm:max-w-md">
        <div className="adv-drawer-handle" />
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-slate-900">{t('advisorPortal.filterTitle', { defaultValue: 'Filters' })}</h2>
          <button onClick={() => setIsFilterDrawerOpen(false)} className="p-2 -mr-2 text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="space-y-5 flex-1 overflow-y-auto pr-1 overscroll-contain">
          {tab === 'active' ? (
            <>
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('advisorPortal.filterStatusLabel')}</label>
                <StatusDropdown
                  value={opStatusFilter}
                  onChange={setOpStatusFilter}
                  options={STATUS_FILTER_OPTIONS}
                  t={t}
                />
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('advisorPortal.showingLabel')}</label>
                <AdvisorViewDropdown
                  value={advisorView}
                  onChange={(v) => {
                    setAdvisorView(v);
                    if (v !== 'all') setAdvisorFilter('All');
                  }}
                  myLabel={t('advisorPortal.myApplications')}
                  allLabel={t('advisorPortal.allApplications')}
                  groupLabel={t('advisorPortal.specificAdvisor')}
                  advisorOptions={otherAdvisorOptions}
                  t={t}
                  advisorNumberKey="advisorPortal.advisorNumber"
                />
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('advisorPortal.filterSortLabel')}</label>
                <div className="flex gap-2">
                  {[
                    { value: 'desc', label: t('advisorPortal.sortStatusDateNewest') },
                    { value: 'asc', label: t('advisorPortal.sortStatusDateOldest') }
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setStatusDateSort(s.value)}
                      className={`flex-1 flex items-center justify-center px-3 py-2.5 rounded-xl border text-xs font-medium transition-all ${statusDateSort === s.value
                        ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                        : 'border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${unassignedOnly ? 'bg-slate-900 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>
                    <i className="ri-user-unfollow-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t('advisorPortal.unassigned')}</p>
                    <p className="text-xs text-slate-500">{t('advisorPortal.unassignedDesc', { defaultValue: 'Show unassigned only' })}</p>
                  </div>
                </div>
                <button
                  onClick={() => setUnassignedOnly(!unassignedOnly)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${unassignedOnly ? 'bg-emerald-500' : 'bg-slate-200'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${unassignedOnly ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-amber-50/50 rounded-2xl border border-amber-100">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${followUpOnly ? 'bg-amber-500 text-white' : 'bg-white text-amber-400 border border-amber-100'}`}>
                    <i className="ri-alarm-warning-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900">{t('advisorPortal.needsAttention')}</p>
                    <p className="text-xs text-amber-600/70">{t('advisorPortal.needsAttentionDesc', { defaultValue: 'Critical items' })}</p>
                  </div>
                </div>
                <button
                  onClick={() => setFollowUpOnly(!followUpOnly)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${followUpOnly ? 'bg-amber-500' : 'bg-slate-200'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${followUpOnly ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('advisorPortal.filterStatusLabel')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'all', label: t('advisorPortal.dirAll') },
                    { value: 'follow-up', label: t('advisorPortal.dirFollowUp') },
                    { value: 'renewal', label: t('advisorPortal.dirRenewal') },
                    { value: 'no-case', label: t('advisorPortal.dirInquiry') },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setDirStatus(opt.value)}
                      className={`flex items-center justify-center px-3 py-2.5 rounded-xl border text-xs font-medium transition-all ${dirStatus === opt.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                        : 'border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100 flex gap-3">
          {(hasActiveFilters || (tab === 'directory' && dirStatus !== 'all')) && (
            <button
              onClick={() => {
                if (tab === 'active') clearActiveCasesFilters();
                else { setDirStatus('all'); setDirQuery(''); }
              }}
              className="flex-1 h-12 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all"
            >
              {t('advisorPortal.clearAll')}
            </button>
          )}
          <button
            onClick={() => setIsFilterDrawerOpen(false)}
            className="flex-[2] h-12 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 active:scale-[0.98] transition-all"
          >
            {t('advisorPortal.showResults', { count: tab === 'active' ? casesTotal : filteredUsers.length, defaultValue: 'Apply Filters' })}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {typeof document !== 'undefined' ? (
        <>
          {createPortal(filterDrawer, document.body)}
        </>
      ) : null}
      <div className="min-h-screen bg-slate-50 flex flex-col min-w-0 max-w-[100vw]">
        <TopNav advisorName={advisorName} />
        <main
          className={`flex-1 w-full min-w-0 max-w-screen-2xl mx-auto px-3 py-4 sm:px-5 sm:py-6 md:px-8 md:py-8`}
        >
          <div className="mb-5 md:mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight">
                {tab === 'active' ? t('advisorPortal.activeCasesTitle') : t('advisorPortal.customerSearchTitle')}
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-prose">
                {tab === 'active' ? t('advisorPortal.activeCasesSubtitle') : t('advisorPortal.customerSearchSubtitle')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch gap-3 w-full min-w-0 lg:w-auto lg:max-w-none lg:shrink-0">
              <div className="flex w-full sm:w-auto min-w-0 items-center bg-slate-200 p-1 rounded-lg gap-0.5">
                <button
                  onClick={() => setTab('active')}
                  className={`flex flex-1 sm:flex-initial min-w-0 justify-center items-center gap-2 text-sm px-3 sm:px-4 py-2.5 rounded-md transition-all cursor-pointer font-medium touch-manipulation ${tab === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <i className="ri-folder-2-line text-sm shrink-0" />
                  <span className="truncate">{t('advisorPortal.tabActiveCases')}</span>
                </button>
                <button
                  onClick={() => setTab('directory')}
                  className={`flex flex-1 sm:flex-initial min-w-0 justify-center items-center gap-2 text-sm px-3 sm:px-4 py-2.5 rounded-md transition-all cursor-pointer font-medium touch-manipulation ${tab === 'directory' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <i className="ri-contacts-book-2-line text-sm shrink-0" />
                  <span className="truncate">{t('advisorPortal.tabCustomerSearch')}</span>
                </button>
              </div>
              <Link
                to="/customer/new"
                className={`inline-flex w-full sm:w-auto min-h-[44px] min-w-0 shrink-0 items-center justify-center gap-1.5 text-sm font-medium bg-slate-900 text-white px-3.5 py-2.5 rounded-lg hover:bg-[#0b1833] transition-colors cursor-pointer touch-manipulation ${tab === 'active' ? 'hidden sm:inline-flex' : ''}`}
              >
                <i className="ri-user-add-line text-sm shrink-0" /> <span className="truncate">{t('advisorPortal.addCustomer')}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block shrink-0 ml-0.5 opacity-90" />
              </Link>
            </div>
          </div>

          {compactFiltersBar}

          {tab === 'active' && (
            <AdvisorWorkbenchBrief
              productionName={activeProject?.name || ''}
              cases={cases}
              summary={casesSummary}
              onFilterReady={() => {
                setOpStatusFilter('ready_to_submit');
                setFollowUpOnly(false);
                setCasesPage(1);
              }}
              onFilterAttention={() => {
                setFollowUpOnly(true);
                setCasesPage(1);
              }}
            />
          )}

          {tab === 'active' && (
            <>
              {/* Hidden per request: Active Cases KPI summary row */}
              {/*
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Open cases', value: activeCaseKpis.open, icon: 'ri-folder-2-line', tone: 'text-slate-700' },
                { label: 'Needs attention', value: activeCaseKpis.needsAttention, icon: 'ri-alarm-warning-line', tone: 'text-amber-700' },
                { label: 'Renewals', value: activeCaseKpis.renewals, icon: 'ri-calendar-event-line', tone: 'text-violet-700' },
                { label: 'Families', value: activeCaseKpis.families, icon: 'ri-team-line', tone: 'text-emerald-700' },
              ].map((k) => (
                <div key={k.label} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 ${k.tone}`}>
                    <i className={`${k.icon} text-lg`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide truncate">{k.label}</p>
                    <p className="text-lg font-semibold text-slate-900 tabular-nums">{k.value}</p>
                  </div>
                </div>
              ))}
            </div>
            */}
              <div className="hidden flex-col gap-3 sm:flex sm:flex-row sm:items-center sm:justify-between mb-4 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm text-slate-500 shrink-0">{t('advisorPortal.showingLabel')}</span>
                  <div className="relative w-full sm:w-[200px] min-w-0 max-w-full">
                    <select
                      value={advisorView}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAdvisorView(v);
                        if (v !== 'all') setAdvisorFilter('All');
                      }}
                      className="w-full text-sm font-medium border border-slate-200 rounded-lg pl-3 pr-10 py-2.5 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 cursor-pointer appearance-none min-h-[44px] max-w-full"
                    >
                      <option value="my">{t('advisorPortal.myApplications')}</option>
                      <optgroup label={t('advisorPortal.specificAdvisor')}>
                        {otherAdvisorOptions.map((a) => (
                          <option key={a.id} value={`advisor:${a.id}`}>
                            {a.name || a.email || t('advisorPortal.advisorNumber', { id: a.id })}
                          </option>
                        ))}
                      </optgroup>
                      <option value="all">{t('advisorPortal.allApplications')}</option>
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-slate-400 text-sm" />
                    </div>
                  </div>
                </div>
                {assignedLabel ? (
                  <p className="text-sm text-slate-500 sm:text-right min-w-0 break-words">
                    {t('advisorPortal.assignedTo')} <span className="font-semibold text-slate-800">{assignedLabel}</span>
                  </p>
                ) : null}
              </div>

              <div
                ref={activeFiltersBlockRef}
                className="adv-filter-card hidden sm:block scroll-mt-20"
              >
                {/* Search */}
                <div className="adv-input-wrap mb-3">
                  <label htmlFor={activeSearchId} className="sr-only">{t('advisorPortal.searchPlaceholderActive')}</label>
                  <i className="ri-search-line adv-input-icon" aria-hidden />
                  <input
                    id={activeSearchId}
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('advisorPortal.searchPlaceholderActive')}
                    className="adv-input"
                  />
                </div>

                {/* Filter row */}
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {/* Status select */}
                  <div className="adv-select-wrap" style={{ width: 140 }}>
                    <label htmlFor={activeStatusFilterId} className="sr-only">{t('advisorPortal.filterStatusLabel')}</label>
                    <select id={activeStatusFilterId} value={opStatusFilter} onChange={(e) => setOpStatusFilter(e.target.value)} className="adv-select">
                      {STATUS_FILTER_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line adv-select-arrow" aria-hidden />
                  </div>

                  {/* Sort select */}
                  <div className="adv-select-wrap" style={{ width: 170 }}>
                    <label htmlFor={activeSortId} className="sr-only">{t('advisorPortal.filterSortLabel')}</label>
                    <select id={activeSortId} value={statusDateSort} onChange={(e) => setStatusDateSort(e.target.value)} className="adv-select">
                      <option value="desc">{t('advisorPortal.sortStatusDateNewest')}</option>
                      <option value="asc">{t('advisorPortal.sortStatusDateOldest')}</option>
                    </select>
                    <i className="ri-arrow-down-s-line adv-select-arrow" aria-hidden />
                  </div>

                  {/* Case type badge */}
                  <span className="adv-toggle-btn" style={{ cursor: 'default', color: '#64748b' }} title={caseTypeLabel}>
                    <i className="ri-file-list-3-line" aria-hidden />
                    <span className="truncate" style={{ maxWidth: 160 }}>{caseTypeLabel}</span>
                  </span>

                  {/* Unassigned toggle */}
                  <button type="button" onClick={() => setUnassignedOnly((v) => !v)}
                    className={`adv-toggle-btn${unassignedOnly ? ' adv-toggle-active' : ''}`}>
                    <i className="ri-user-unfollow-line" aria-hidden />
                    <span>{t('advisorPortal.unassigned')}</span>
                  </button>

                  {/* Needs attention toggle */}
                  <button type="button" onClick={() => setFollowUpOnly((v) => !v)}
                    className={`adv-toggle-btn${followUpOnly ? ' adv-toggle-warn' : ''}`}>
                    <i className="ri-alarm-warning-line" aria-hidden />
                    <span>{t('advisorPortal.needsAttention')}</span>
                  </button>

                  {/* Count pill */}
                  <span className="adv-count-pill ml-auto">
                    {casesTotal === 1
                      ? t('advisorPortal.casesCountOne')
                      : t('advisorPortal.casesCount', { count: casesTotal })}
                  </span>

                  {/* Clear */}
                  {hasActiveFilters ? (
                    <button type="button" onClick={clearActiveCasesFilters} className="adv-clear-btn">
                      <i className="ri-close-circle-line" aria-hidden />
                      {t('advisorPortal.clearAll')}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden relative">
                {loading || casesLoading ? (
                  <SkeletonApplicationsTable rows={8} />
                ) : (
                  <AdvisorApplicationsTable
                    cases={cases}
                    advisorName={advisorName}
                    listReturnTo={casesListReturnTo}
                  />
                )}
                <div className="px-3 sm:px-4 border-t border-slate-100">
                  <CasesListPagination
                    page={casesPage}
                    totalPages={casesTotalPages}
                    totalItems={casesTotal}
                    pageSize={ACTIVE_CASES_PAGE_SIZE}
                    onPageChange={setCasesPage}
                    disabled={loading}
                    loading={casesLoading}
                  />
                </div>
              </div>
            </>
          )}

          {tab === 'directory' && (
            <>
              <div className="adv-filter-card hidden sm:block mb-4 md:mb-5 min-w-0">
                {/* Directory search */}
                <div className="adv-input-wrap mb-3">
                  <i className="ri-search-line adv-input-icon" />
                  <input
                    type="text"
                    value={dirQuery}
                    onChange={(e) => setDirQuery(e.target.value)}
                    placeholder={t('advisorPortal.dirSearchPlaceholder')}
                    className="adv-input"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  {/* Status tab group */}
                  <div className="adv-tab-bar flex-1 sm:flex-initial overflow-x-auto">
                    {[
                      { value: 'all', label: t('advisorPortal.dirAll') },
                      { value: 'follow-up', label: t('advisorPortal.dirFollowUp') },
                      { value: 'renewal', label: t('advisorPortal.dirRenewal') },
                      { value: 'no-case', label: t('advisorPortal.dirInquiry') },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDirStatus(opt.value)}
                        className={`adv-tab-btn${dirStatus === opt.value ? ' adv-tab-active' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <span className="adv-count-pill ml-auto">
                    {dirTotal === 1
                      ? t('advisorPortal.customersCountOne')
                      : t('advisorPortal.customersCount', { count: dirTotal })}
                  </span>
                  {(dirQuery || dirStatus !== 'all') ? (
                    <button
                      type="button"
                      onClick={() => {
                        setDirQuery('');
                        setDirStatus('all');
                        setDirPage(1);
                      }}
                      className="adv-clear-btn"
                    >
                      <i className="ri-close-circle-line" />
                      {t('advisorPortal.clearAll')}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden relative">
                {directoryLoading ? (
                  <SkeletonDirectoryTable rows={8} />
                ) : (
                  <AdvisorCustomerDirectoryTable
                    users={users}
                    onDeleteUser={handleDeleteUser}
                    deletingUserId={deletingUserId}
                  />
                )}
                <div className="px-3 sm:px-4 border-t border-slate-100">
                  <CasesListPagination
                    page={dirPage}
                    totalPages={dirTotalPages}
                    totalItems={dirTotal}
                    pageSize={DIRECTORY_PAGE_SIZE}
                    onPageChange={setDirPage}
                    disabled={directoryLoading}
                    loading={directoryLoading}
                    itemLabelSingular="customer"
                    itemLabelPlural="customers"
                    loadingLabel="Loading customers…"
                  />
                </div>
              </div>
            </>
          )}
        </main>
        {deleteModalUser ? (
          <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center bg-slate-900/45 p-0 sm:p-4 overflow-y-auto overscroll-contain">
            <div
              className="w-full sm:max-w-md max-h-[min(92dvh,calc(100vh-1rem))] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white border border-slate-200 sm:shadow-2xl shadow-2xl p-4 sm:p-5 my-0 sm:my-auto pb-[max(1rem,env(safe-area-inset-bottom,0px))] sm:pb-5"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-user-title"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                  <i className="ri-delete-bin-line text-lg" />
                </div>
                <div className="min-w-0">
                  <h3 id="delete-user-title" className="text-sm font-semibold text-slate-900">
                    {t('advisorPortal.confirmDeleteTitle', { defaultValue: 'Delete customer' })}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {t('advisorPortal.deleteUserConfirm', {
                      name: deleteModalUser.name || deleteModalUser.email || `User ${deleteModalUser.id}`,
                    })}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDeleteModalUser(null)}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 text-sm font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors touch-manipulation"
                >
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 text-sm font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors touch-manipulation"
                >
                  {t('common.delete', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
